import React, { useEffect, useState, useMemo, memo } from "react";
import {
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme
} from "@mui/material";
import { IMsalContext, useMsal } from "@azure/msal-react";
import { ApiService } from "../../../Services/ApiService";
import { getFormattedDate } from '../../../Utils/UnitsHelper';
import logger from '../../../Services/LoggingService';
import { IDesignSession } from "../../../Models/IDesignSession";
import { createFlexStyles, createStatusStyles } from '../../../theme/layoutStyles';

/**
 * History list component for displaying the history list - Material UI version
 *
 * @return {*} 
 */
export const HistoryList: React.FC = () => {
  const theme = useTheme();
  const flexStyles = createFlexStyles(theme);
  const statusStyles = createStatusStyles(theme);

  const [pageSize, setPageSize] = useState(10);
  const [historyList, setHistoryList] = useState<IDesignSession[]>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const ctx: IMsalContext = useMsal();
  const dataService = useMemo(() => new ApiService(ctx), [ctx]);

  // Page size options
  const pageSizeOptions = [
    { value: 10, label: '10' },
    { value: 25, label: '25' },
    { value: 50, label: '50' },
    { value: 100, label: '100' }
  ];

  // OPTIMIZED: Memoize styles to prevent recreation on every render
  const styles = useMemo(() => ({
    container: {
      ...flexStyles.flexColumn,
      gap: theme.spacing(2),
      height: '100%',
      width: 'auto'
      // overflow: 'scroll',
    },
    header: flexStyles.flexCenterBetween,
    title: {
      fontSize: '1.5rem',
      fontWeight: 500,
      margin: 0,
      color: theme.palette.text.primary,
    },
    pageSizeContainer: {
      ...flexStyles.flexCenterGap1,
      alignSelf: 'flex-end'
    },
    pageSizeSelect: {
      minWidth: 80, // Reduced from 100
      '& .MuiSelect-select': {
        paddingTop: theme.spacing(0.5), // Reduced padding
        paddingBottom: theme.spacing(0.5),
      },
    },
    tableContainer: {
    //   margin: theme.spacing(2), // Reduced margins
      boxShadow: theme.shadows[1],
      borderRadius: theme.shape.borderRadius,
      flex: 1,
      // overflow: 'auto',
    //   maxHeight: 'calc(100vh - 200px)', // Ensure it fits in viewport
    },
    table: {
      minWidth: 'auto', // Remove minimum width constraint
      tableLayout: 'fixed' as const, // Fixed layout for better control
    },
    tableHead: {
      backgroundColor: theme.palette.grey[50],
      position: 'sticky' as const,
      top: 0,
      zIndex: 1,
    },
    tableHeadCell: {
      fontWeight: 600,
      fontSize: '0.8rem', // Slightly smaller
      color: theme.palette.text.primary,
      borderBottom: `2px solid ${theme.palette.divider}`,
      padding: theme.spacing(1), // Reduced padding
      '&:nth-of-type(1)': { width: '35%' }, // Uploaded by
      '&:nth-of-type(2)': { width: '25%' }, // Created
      '&:nth-of-type(3)': { width: '20%' }, // Product Line
      '&:nth-of-type(4)': { width: '20%' }, // Position
    },
    tableCell: {
      fontSize: '0.8rem', // Slightly smaller
      color: theme.palette.text.primary,
      padding: theme.spacing(1), // Reduced padding
      whiteSpace: 'nowrap' as const,
      // overflow: 'hidden',
      textOverflow: 'ellipsis',
      '&:nth-of-type(1)': { 
        width: '35%',
        maxWidth: '200px', // Prevent extremely long usernames
      },
      '&:nth-of-type(2)': { 
        width: '25%',
        maxWidth: '150px',
      },
      '&:nth-of-type(3)': { 
        width: '20%',
        maxWidth: '120px',
      },
      '&:nth-of-type(4)': { 
        width: '20%',
        maxWidth: '120px',
      },
    },
    tableRow: {
      '&:nth-of-type(even)': {
        backgroundColor: theme.palette.action.hover,
      },
      '&:hover': {
        backgroundColor: theme.palette.action.selected,
      },
      height: '48px', // Fixed row height for consistency
    },
    loadingContainer: {
      ...statusStyles.loadingContainer,
      width: '90vw' // Custom override for this component
    },
    loadingText: {
      color: theme.palette.text.secondary,
      fontSize: '0.875rem',
    },
  }), [theme, flexStyles.flexCenterBetween, flexStyles.flexCenterGap1, flexStyles.flexColumn, statusStyles.loadingContainer]);

  // Handle page size change
  const handlePageSizeChange = (event: SelectChangeEvent<number>) => {
    setPageSize(event.target.value as number);
  };

  // Format date for display
  const formatDate = (dateInput: string | Date) => {
    if (!dateInput) return '-';
    
    // If it's already a string, pass it directly
    if (typeof dateInput === 'string') {
      return getFormattedDate(dateInput);
    }
    
    // If it's a Date object, convert to ISO string first
    return getFormattedDate(dateInput.toISOString());
  };

  useEffect(() => {
    setIsLoading(true);
    async function getHistoryData() {
      try {
        const response = await dataService.getAsync(`designSessions/user/history?take=${pageSize}`);
        const data = await response.json();

        const historyData: IDesignSession[] = data;
        setHistoryList(historyData);
      } catch (error) {
        logger.error('Failed to fetch history data', error as Error, {
          pageSize
        }, {
          component: 'HistoryList',
          method: 'getHistoryData'
        });
        setHistoryList([]);
      } finally {
        setIsLoading(false);
      }
    }

    getHistoryData();
  }, [pageSize, dataService]);

  return (
    <Box sx={styles.container}>
      {/* Header with title and page size selector */}
      <Box sx={styles.header}>
        <Typography variant="h4" sx={styles.title}>
          History
        </Typography>
      </Box>

      {/* Table content */}
      {!isLoading ? (
        <>
        <MemoizedPageSizeSelector
          pageSize={pageSize}
          pageSizeOptions={pageSizeOptions}
          onPageSizeChange={handlePageSizeChange}
          styles={styles}
          theme={theme}
        />
          {historyList && historyList.length > 0 ? (
            <TableContainer component={Paper} sx={styles.tableContainer}>
              <Table sx={styles.table} size="small">
                <TableHead sx={styles.tableHead}>
                  <TableRow>
                    <TableCell sx={styles.tableHeadCell}>
                      Uploaded by
                    </TableCell>
                    <TableCell sx={styles.tableHeadCell}>
                      Created
                    </TableCell>
                    <TableCell sx={styles.tableHeadCell}>
                      Product Line
                    </TableCell>
                    <TableCell sx={styles.tableHeadCell}>
                      Position
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historyList.map((item, index) => (
                    <MemoizedHistoryTableRow
                      key={index}
                      item={item}
                      styles={styles}
                      formatDate={formatDate}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={styles.loadingContainer}>
              <Typography variant="body1" sx={styles.loadingText}>
                No history data available
              </Typography>
            </Box>
          )}
        </>
      ) : (
        <Box sx={styles.loadingContainer}>
          <CircularProgress size={48} />
          <Typography variant="body1" sx={styles.loadingText}>
            Loading history...
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// OPTIMIZED: Memoized table row component to prevent unnecessary re-renders
const MemoizedHistoryTableRow = memo<{
  item: IDesignSession;
  styles: any;
  formatDate: (date: string | Date) => string;
}>(({ item, styles, formatDate }) => {
  return (
    <TableRow sx={styles.tableRow}>
      <TableCell sx={styles.tableCell} title={item.user || ''}>
        {item.user || '-'}
      </TableCell>
      <TableCell sx={styles.tableCell} title={item.createdDate ? formatDate(item.createdDate) : ''}>
        {item.createdDate ? formatDate(item.createdDate) : '-'}
      </TableCell>
      <TableCell sx={styles.tableCell} title={item.productLine || ''}>
        {item.productLine || '-'}
      </TableCell>
      <TableCell sx={styles.tableCell} title={item.alfaLavalPosition || ''}>
        {item.alfaLavalPosition || '-'}
      </TableCell>
    </TableRow>
  );
});

MemoizedHistoryTableRow.displayName = 'MemoizedHistoryTableRow';

// OPTIMIZED: Memoized page size selector component
const MemoizedPageSizeSelector = memo<{
  pageSize: number;
  pageSizeOptions: Array<{ value: number; label: string }>;
  onPageSizeChange: (event: SelectChangeEvent<number>) => void;
  styles: any;
  theme: any;
}>(({ pageSize, pageSizeOptions, onPageSizeChange, styles, theme }) => {
  return (
    <Box sx={styles.pageSizeContainer}>
      <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
        Page size
      </Typography>
      <FormControl size="small" sx={styles.pageSizeSelect}>
        <InputLabel id="page-size-label">Size</InputLabel>
        <Select
          labelId="page-size-label"
          value={pageSize}
          label="Size"
          onChange={onPageSizeChange}
        >
          {pageSizeOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
});

MemoizedPageSizeSelector.displayName = 'MemoizedPageSizeSelector';

export default HistoryList; 